import React, { useState, useEffect } from 'react';
import styles from './PackagePurchaseFlow.module.css';
import {
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography
} from '@mui/material';
import { DataProviderInstance } from '../../api/DataProvider';
import PopupBase from '../../popup/base/PopupBase';
import CustomButton from '../../components/buttons/CustomButton';
import ColorPalette from '../../colorpalette/ColorPalette';
import PlanUpgradeModal from '../subscription/PlanUpgradeModal';
import useSubscriptionFeatures from '../../hooks/useSubscriptionFeatures';
import { SUBSCRIPTION_PERMISSIONS } from '../../consts/SubscriptionFeatures';
import { PROFESSIONAL_PLAN_NAME } from '../subscription/planLockTexts';
import { formatMoneyInput } from '../../utils/moneyUtils';

/** 1200.5 -> "R$ 1.200,50". */
const formatBRL = (value) => `R$ ${formatMoneyInput(Number(value) || 0)}`;

const PackagePurchaseFlow = ({ 
  onPurchaseComplete,
  companyConfig,
  walletId = null, // opcional, se não fornecido, será apenas visualização
  open = false,
  onClose,
}) => {
  const { GetServicePackages, PurchaseServicePackage } = DataProviderInstance();
  // Cadeado por plano nos dois pontos de uso (carteira "Adicionar créditos" e
  // fatura "Adicionar pacote"): no lugar da lista, o modal de upgrade.
  const { hasPermission } = useSubscriptionFeatures();
  const packagesLocked = !hasPermission(SUBSCRIPTION_PERMISSIONS.SERVICE_PACKAGES);

  const [packages, setPackages] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(companyConfig.PaymentOptions[0]?.Id);
  const [purchaseConfirmationOpen, setPurchaseConfirmationOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  useEffect(() => {
    if (open && !packagesLocked) {
      GetServicePackages((response) => {
        if (response.success) {
          setPackages(response.data || []);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, packagesLocked]);

  const handlePurchaseClick = (packageData) => {
    setSelectedPackage(packageData);
    setPurchaseConfirmationOpen(true);
  };

  const handleConfirmPurchase = () => {
    if (!selectedPackage || !walletId) return;

    PurchaseServicePackage(
      walletId,
      selectedPackage.package_id,
      selectedPaymentMethod,
      'PENDING',
      (response) => {
        if (response.success) {
          setPurchaseConfirmationOpen(false);
          if (onPurchaseComplete) {
            onPurchaseComplete(response);
          }
          if (onClose) {
            onClose();
          }
        }
      }
    );
  };

  // Calculate the total value of procedures in a package
  const calculateTotalProceduresValue = (pkg) => {
    return (pkg.procedures || []).reduce((total, proc) => {
      const procedure = companyConfig.Procedures.find(p => p.Id == proc.procedure_id);
      return total + (procedure?.Price || 0) * proc.quantity;
    }, 0);
  };

  // Calculate discount percentage
  const calculateDiscountPercentage = (totalValue, packagePrice) => {
    if (!totalValue || totalValue <= 0) return 0;
    const difference = totalValue - packagePrice;
    return Math.max(0, (difference / totalValue) * 100).toFixed(1);
  };

  const renderPackagesList = () => (
    <Grid container spacing={3} className={styles.packagesGrid}>
      {(packages || [])
        .filter(pkg => pkg.active)
        .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
        .map((pkg) => {
          const totalProceduresValue = calculateTotalProceduresValue(pkg);
          const discountPercentage = calculateDiscountPercentage(totalProceduresValue, pkg.price);
          const hasDiscount = totalProceduresValue > pkg.price;
          
          return (
            <Grid item xs={12} sm={6} md={4} key={pkg.package_id}>
              <div className={styles.packageCard}>
                <div className={styles.packageHeader}>
                  <div>
                    <h4>{pkg.name}</h4>
                    <span className={`${styles.packageStatus} ${pkg.active ? styles.active : ''}`}>
                      {pkg.active ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                  <div className={styles.priceContainer}>
                    {hasDiscount && (
                      <div className={styles.originalPriceTag}>
                        {formatBRL(totalProceduresValue)}
                      </div>
                    )}
                    <span className={styles.packagePrice}>
                      {formatBRL(pkg.price)}
                    </span>
                    {hasDiscount && (
                      <div className={styles.discountBadge}>
                        {discountPercentage}% OFF
                      </div>
                    )}
                  </div>
                </div>

                <p className={styles.packageDescription}>{pkg.description || "Pacote de serviços"}</p>

                <div className={styles.packageDetails}>
                  <div>
                    <span className={styles.detailLabel}>Procedimentos</span>
                    <div className={styles.proceduresList}>
                      {(pkg.procedures || []).map((proc, index) => (
                        <div key={index} className={styles.procedureItem}>
                          <span>{companyConfig.Procedures.find(p => p.Id == proc.procedure_id)?.Label || 'Procedimento não encontrado'}</span>
                          <span className={styles.quantityBadge}>{proc.quantity}x</span>
                        </div>
                      ))}
                      {(pkg.procedures || []).length === 0 && (
                        <div className={styles.emptyProcedures}>Nenhum procedimento incluído</div>
                      )}
                    </div>
                  </div>
                  <div className={styles.validityContainer}>
                    <span className={styles.detailLabel}>Validade</span>
                    <span className={styles.detailValue}>
                      {pkg.validity_days === 0 || pkg.validity_days === null ? 'Não expira' : `${pkg.validity_days} dias`}
                    </span>
                  </div>
                </div>

                {pkg.active && walletId && (
                  <CustomButton
                    variant="contained"
                    style="primary"
                    label="Comprar"
                    fullWidth
                    className={styles.packageButton}
                    onClick={() => handlePurchaseClick(pkg)}
                  />
                )}
              </div>
            </Grid>
          );
        })}
      {packages.filter(pkg => pkg.active).length === 0 && (
        <div className={styles.noPackages}>
          <p>Nenhum pacote disponível no momento.</p>
        </div>
      )}
    </Grid>
  );

  const renderPurchaseConfirmation = () => {
    if (!selectedPackage) return null;
    
    const totalProceduresValue = calculateTotalProceduresValue(selectedPackage);
    const discountPercentage = calculateDiscountPercentage(totalProceduresValue, selectedPackage.price);
    const hasDiscount = totalProceduresValue > selectedPackage.price;
    
    return (
      <div className={styles.confirmationContainer}>
        <h3 className={styles.confirmationTitle}>{selectedPackage.name}</h3>
        
        <div className={styles.priceSection}>
          {hasDiscount && (
            <div className={styles.originalPrice}>
              Valor original: {formatBRL(totalProceduresValue)}
            </div>
          )}
          
          <div className={styles.finalPrice}>
            Valor: {formatBRL(selectedPackage.price)}
            {hasDiscount && (
              <span className={styles.discountTag}>
                (desconto de {discountPercentage}%)
              </span>
            )}
          </div>
        </div>
        
        <div className={styles.proceduresSection}>
          <h4 className={styles.sectionTitle}>Procedimentos incluídos:</h4>
          <div className={styles.procedureConfirmList}>
            {(selectedPackage.procedures || []).map((proc, index) => (
              <div key={index} className={styles.procedureConfirmItem}>
                <span className={styles.bulletPoint}>•</span>
                <span className={styles.procedureName}>
                  {companyConfig.Procedures.find(p => p.Id == proc.procedure_id)?.Label || 'Procedimento não encontrado'}
                </span>
                <span className={styles.procedureQuantity}>({proc.quantity}x)</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className={styles.validitySection}>
          <span className={styles.validityLabel}>Validade:</span>
          <span className={styles.validityValue}>
            {selectedPackage.validity_days === 0 || selectedPackage.validity_days === null ? 'Não expira' : `${selectedPackage.validity_days} dias`}
          </span>
        </div>
        
        <div className={styles.paymentSection}>
          <h4 className={styles.paymentLabel}>Forma de Pagamento</h4>
          <div className={styles.paymentGrid}>
            {companyConfig.PaymentOptions.map((option) => (
              <div
                key={option.Id}
                className={`${styles.paymentOption} ${selectedPaymentMethod === option.Id ? styles.selectedPayment : ''}`}
                onClick={() => setSelectedPaymentMethod(option.Id)}
              >
                <Radio
                  checked={selectedPaymentMethod === option.Id}
                  onChange={() => setSelectedPaymentMethod(option.Id)}
                  sx={{
                    color: ColorPalette.main,
                    '&.Mui-checked': {
                      color: ColorPalette.main,
                    },
                    padding: '4px',
                  }}
                />
                <span>{option.Label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  if (packagesLocked) {
    return (
      <PlanUpgradeModal
        isOpen={open}
        onClose={onClose}
        featureName="Pacotes de serviços"
        type="permission"
        planName={PROFESSIONAL_PLAN_NAME}
        plural
      />
    );
  }

  return (
    <>
      {/* PopupBase de Pacotes */}
      {open && !purchaseConfirmationOpen && (
        <PopupBase
          Title="Pacotes Disponíveis"
          Content={renderPackagesList()}
          NegativeButton={
            <CustomButton
              label="Fechar"
              style="primary"
              variant="outlined"
              onClick={onClose}
            />
          }
          OnClose={onClose}
          maxWidth="xl"
        />
      )}

      {/* PopupBase de Confirmação de Compra */}
      {purchaseConfirmationOpen && (
        <PopupBase
          Title="Confirmar Compra"
          Content={renderPurchaseConfirmation()}
          NegativeButton={
            <CustomButton
              label="Cancelar"
              style="primary"
              variant="outlined"
              onClick={() => setPurchaseConfirmationOpen(false)}
            />
          }
          PositiveButton={
            <CustomButton
              label="Confirmar Compra"
              style="primary"
              variant="contained"
              onClick={handleConfirmPurchase}
            />
          }
          OnClose={() => setPurchaseConfirmationOpen(false)}
        />
      )}
    </>
  );
};

export default PackagePurchaseFlow;